import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import LanguageDetector from "i18next-browser-languagedetector";
import en from "./i18n/en";
import gr from "./i18n/gr";
import it from "./i18n/it";
import fr from "./i18n/fr";
import de from "./i18n/de";
import nl from "./i18n/nl";
import es from "./i18n/es";
import pt from "./i18n/pt";
import he from "./i18n/he";
import ja from "./i18n/ja";
import th from "./i18n/th";
import zh from "./i18n/zh";
import lt from "./i18n/lt";
import ar from "./i18n/ar";
import id from "./i18n/id";
import ms from "./i18n/ms";
import hi from "./i18n/hi";
import fil from "./i18n/fil";
import ko from "./i18n/ko";
import vi from "./i18n/vi";
import { filterAllowedLanguages } from "./config/supportedLanguages";

const defaultLang = import.meta.env.VITE_APP_DEFAULT_LANG ?? "EN-US";

const allAvailableResources = [
  "en-US",
  "en-GB",
  "GR",
  "IT",
  "FR",
  "DE",
  "NL",
  "ES",
  "PT",
  "HE",
  "JA",
  "TH",
  "ZH",
  "LT",
  "AR",
  "ID",
  "MS",
  "HI",
  "FIL",
  "KO",
  "VI",
];

const availableResources = filterAllowedLanguages(allAvailableResources);

function normalizeLanguageCode(code: string): string {
  const trimmed = code.trim();
  if (!trimmed) {
    return "en-US";
  }

  const base = trimmed.split("-")[0].toLowerCase();
  if (base === "en") {
    const englishResource =
      availableResources.find((resource) => resource.toLowerCase().startsWith("en-")) ?? "en-US";
    return englishResource;
  }

  if (availableResources.includes(trimmed)) {
    return trimmed;
  }

  const caseInsensitiveMatch = availableResources.find(
    (resource) => resource.toUpperCase() === trimmed.toUpperCase(),
  );
  if (caseInsensitiveMatch) {
    return caseInsensitiveMatch;
  }

  const normalizedDefault = normalizeLanguageCode(defaultLang);
  return availableResources.includes(normalizedDefault) ? normalizedDefault : "en-US";
}

i18n
  // detect user language
  // learn more: https://github.com/i18next/i18next-browser-languageDetector
  .use(LanguageDetector)
  // pass the i18n instance to react-i18next.
  .use(initReactI18next)
  // init i18next
  // for all options read: https://www.i18next.com/overview/configuration-options
  .init({
    fallbackLng: normalizeLanguageCode(defaultLang),
    debug: false,
    resources: {
      "en-US": { translation: en },
      "en-GB": { translation: en },
      GR: { translation: gr },
      IT: { translation: it },
      FR: { translation: fr },
      DE: { translation: de },
      NL: { translation: nl },
      ES: { translation: es },
      PT: { translation: pt },
      HE: { translation: he },
      JA: { translation: ja },
      TH: { translation: th },
      ZH: { translation: zh },
      LT: { translation: lt },
      AR: { translation: ar },
      ID: { translation: id },
      MS: { translation: ms },
      HI: { translation: hi },
      FIL: { translation: fil },
      KO: { translation: ko },
      VI: { translation: vi },
    },
    interpolation: { escapeValue: false },
  });

const resolvedLanguage = normalizeLanguageCode(i18n.language);
if (resolvedLanguage !== i18n.language) {
  i18n.changeLanguage(resolvedLanguage);
}

export default i18n;
